import React, { useState } from "react";
import {
	Alert,
	Button,
	Chip,
	Grid,
	Snackbar,
	Typography,
} from "@mui/material";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { OpcionesMenuFunc } from "../../../utils/componentes-utiles/OpcionesMenuFunc";
import { EditarUsuario } from "./EditarUsuario";
import { delUsuario } from "../../../services/apis";

const ROL_CLIENTE = 990;
const ROL_CONTROLADOR = 980;

const normalizeRoleName = (value = "") =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

export const UsuarioCard = ({
	usuario,
	opciones,
	cargarUsuarios,
	departamentos,
	roles,
	clientes,
	delegaciones,
}) => {
	const [openEdit, setOpenEdit] = useState(false);
	const [openConfirm, setOpenConfirm] = useState(false);
	const [openSuccess, setOpenSuccess] = useState(false);
	const [openError, setOpenError] = useState(false);
	const [loadingDelete, setLoadingDelete] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");

	const handleEvent = (opcionId) => {
		if (opcionId === 1) setOpenEdit(true);
		if (opcionId === 2) setOpenConfirm(true);
	};

	const confirmarEliminacion = async () => {
		setLoadingDelete(true);
		try {
			const response = await delUsuario(usuario.id);
			if (response?.success) {
				setOpenConfirm(false);
				setOpenSuccess(true);
				await cargarUsuarios();
			} else {
				setErrorMsg(response?.error || "Error al eliminar el usuario");
				setOpenError(true);
			}
		} catch {
			setErrorMsg("Error de conexion con el servidor");
			setOpenError(true);
		} finally {
			setLoadingDelete(false);
		}
	};

	const nombreCompleto =
		usuario?.nombre_completo?.trim() ||
		`${usuario?.apellidos ?? ""} ${usuario?.nombre ?? ""}`.trim() ||
		usuario?.nombre ||
		"-";

	const esCliente = Number(usuario?.rol || usuario?.id_rol || 0) === ROL_CLIENTE;
	const esControlador = Number(usuario?.rol || usuario?.id_rol || 0) === ROL_CONTROLADOR;
	const esCaboCliente = normalizeRoleName(usuario?.nombre_rol) === "cabo cliente";
	const clientesAsignados = Array.isArray(usuario?.clientes_asignados)
		? usuario.clientes_asignados
		: [];
	const delegacionesAsignadas = Array.isArray(usuario?.delegaciones_asignadas)
		? usuario.delegaciones_asignadas
		: [];

	const accesosLabel = (() => {
		if (!esCliente && !esCaboCliente && !esControlador) {
			return "No aplica";
		}

		const partes = [];

		if (clientesAsignados.length > 0) {
			partes.push(
				`Clientes: ${clientesAsignados
					.map((cliente) => cliente?.nombre)
					.filter(Boolean)
					.join(", ")}`
			);
		}

		if (delegacionesAsignadas.length > 0) {
			partes.push(
				`Delegaciones: ${delegacionesAsignadas
					.map((delegacion) => delegacion?.nombre)
					.filter(Boolean)
					.join(", ")}`
			);
		}

		return partes.length > 0 ? partes.join(" · ") : "Sin accesos asignados";
	})();

	return (
		<>
			<Grid
				container
				size={{ xs: 12 }}
				sx={{
					px: 2,
					py: 1.5,
					borderBottom: "1px solid",
					borderColor: "divider",
					background: "var(--fondoTarjeta)",
					"&:hover": { backgroundColor: "action.hover" },
				}}
			>
				<Grid
					container
					size={{ xs: 12 }}
					alignItems="center"
					sx={{ display: { xs: "flex", sm: "none" } }}
				>
					<Grid item size={{ xs: 2 }}>
						<AccountCircleIcon sx={{ fontSize: 36, color: usuario.color }} />
					</Grid>

					<Grid item size={{ xs: 8 }}>
						<Typography variant="body1" fontWeight={600} noWrap>
							{nombreCompleto}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{usuario.nombre_rol}
						</Typography>
						{esCliente || esCaboCliente || esControlador ? (
							<Typography variant="caption" display="block" color="text.secondary">
								{accesosLabel}
							</Typography>
						) : null}
					</Grid>

					<Grid item size={{ xs: 2 }} sx={{ textAlign: "right" }}>
						<OpcionesMenuFunc
							small
							variant="text"
							opciones={opciones}
							handleEvent={handleEvent}
						/>
					</Grid>
				</Grid>

				<Grid
					container
					size={{ xs: 12 }}
					alignItems="center"
					sx={{ display: { xs: "none", sm: "flex" } }}
				>
					<Grid item size={{ sm: 1 }}>
						<AccountCircleIcon sx={{ fontSize: 32, color: usuario.color }} />
					</Grid>

					<Grid item size={{ sm: 4 }}>
						<Typography variant="body2" fontWeight={500} noWrap>
							{nombreCompleto}
						</Typography>
					</Grid>

					<Grid item size={{ sm: 3 }}>
						<Typography variant="body2">{usuario.nombre_rol}</Typography>
					</Grid>

					<Grid item size={{ sm: 3 }}>
						{esCliente || esCaboCliente || esControlador ? (
							clientesAsignados.length > 0 || delegacionesAsignadas.length > 0 ? (
								<Grid container gap={0.5}>
									{clientesAsignados.slice(0, 2).map((cliente) => (
										<Chip
											key={`usuario-cliente-${usuario.id}-${cliente.id}`}
											size="small"
											label={cliente.nombre || `Cliente ${cliente.id}`}
										/>
									))}
									{delegacionesAsignadas.slice(0, Math.max(0, 2 - clientesAsignados.slice(0, 2).length)).map((delegacion) => (
										<Chip
											key={`usuario-delegacion-${usuario.id}-${delegacion.id}`}
											size="small"
											variant="outlined"
											label={delegacion.nombre || `Delegacion ${delegacion.id}`}
										/>
									))}
									{clientesAsignados.length + delegacionesAsignadas.length > 2 ? (
										<Chip
											size="small"
											variant="outlined"
											label={`+${clientesAsignados.length + delegacionesAsignadas.length - 2}`}
										/>
									) : null}
								</Grid>
							) : (
								<Typography variant="body2" color="text.secondary">
									Sin accesos
								</Typography>
							)
						) : (
							<Typography variant="body2" color="text.secondary">
								No aplica
							</Typography>
						)}
					</Grid>

					<Grid item size={{ sm: 1 }} sx={{ textAlign: "right" }}>
						<OpcionesMenuFunc
							small
							variant="text"
							opciones={opciones}
							handleEvent={handleEvent}
						/>
					</Grid>
				</Grid>

				{openEdit && (
					<EditarUsuario
						open={openEdit}
						setOpen={setOpenEdit}
						cargarUsuarios={cargarUsuarios}
						departamentos={departamentos}
						roles={roles}
						clientes={clientes}
						delegaciones={delegaciones}
						usuarioData={usuario}
					/>
				)}
			</Grid>

			<Snackbar open={openConfirm} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert
					severity="warning"
					action={
						<>
							<Button size="small" onClick={() => setOpenConfirm(false)}>
								Cancelar
							</Button>
							<Button
								size="small"
								color="error"
								disabled={loadingDelete}
								onClick={confirmarEliminacion}
							>
								Eliminar
							</Button>
						</>
					}
				>
					¿Eliminar a <strong>{nombreCompleto}</strong>?
				</Alert>
			</Snackbar>

			<Snackbar open={openSuccess} autoHideDuration={3000} onClose={() => setOpenSuccess(false)}>
				<Alert severity="success">Usuario eliminado correctamente</Alert>
			</Snackbar>

			<Snackbar open={openError} autoHideDuration={4000} onClose={() => setOpenError(false)}>
				<Alert severity="error">{errorMsg}</Alert>
			</Snackbar>
		</>
	);
};
